'use client';

import { type FC, type ReactNode, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguages } from '@/hooks/languages';
import { useComponentVisible } from '@/hooks/visibility';
import { sendOtherLanguageSwitcherEvent } from '@/modules/googleAnalytics/events/otherEvents';
import { useLanguageChange } from '@/modules/localization';
import { useGetCommonStore } from '@/store';
import { IconComponent } from '../IconComponent';
import { ImageComponent } from '../Image';
import { ModalSelect } from '../ModalSelect';
import { SwitchLanguageSelect } from './SwitchLanguageSelect';
import { ScSwitchLanguage, ScSwitchLanguageButton } from './styled';

export interface SwitchLanguageProps {
  variant?: 'full' | 'compact';
  arrowIcon?: ReactNode;
  onClose?: () => void;
  place: 'footer' | 'header';
}

export const SwitchLanguage: FC<SwitchLanguageProps> = ({
  variant = 'full',
  arrowIcon,
  onClose,
  place,
}) => {
  const {
    i18n: { language: currentLang },
  } = useTranslation();
  const useCommonStore = useGetCommonStore();

  const isMobileDevice = useCommonStore.use.isMobileDevice();
  const countryCode = useCommonStore.use.countryCode();
  const [changeLanguage, pending] = useLanguageChange();

  const { ref, openerRef, isComponentVisible, setIsComponentVisible } =
    useComponentVisible(false);

  const isFull = variant === 'full';

  const toggleComponentVisible = useCallback(() => {
    setIsComponentVisible(!isComponentVisible);
  }, [isComponentVisible, setIsComponentVisible]);

  const handleChangeLanguage = useCallback(
    async (newLocale: string | number) => {
      if (typeof newLocale !== 'string') return;

      sendOtherLanguageSwitcherEvent({
        type: place,
        browser_language: navigator?.language,
        country_code: countryCode,
        from: currentLang,
        to: newLocale,
      });

      await changeLanguage(newLocale);

      toggleComponentVisible();
      if (onClose) onClose();
    },
    [
      changeLanguage,
      toggleComponentVisible,
      onClose,
      place,
      countryCode,
      currentLang,
    ]
  );

  const languages = useLanguages();

  const availableLanguages = useMemo(
    () => languages.filter(({ isHidden }) => !isHidden),
    [languages]
  );

  const currentLocale = useMemo(
    () => availableLanguages.find(({ value }) => value === currentLang),
    [availableLanguages, currentLang]
  );

  return (
    <ScSwitchLanguage $active={isComponentVisible}>
      <ScSwitchLanguageButton
        $active={isComponentVisible}
        onClick={toggleComponentVisible}
        ref={openerRef}
        variant={variant}
      >
        <div className="flag">
          <ImageComponent
            alt="flag"
            height={isFull ? 24 : 40}
            src={currentLocale?.icon || ''}
            width={isFull ? 24 : 40}
          />
        </div>
        {isFull && <div className="value">{currentLocale?.name}</div>}
        {arrowIcon ? (
          arrowIcon
        ) : (
          <div className="arrow">
            <IconComponent
              name="whiteArrowRight"
              size={isFull ? '20px' : '16px'}
            />
          </div>
        )}
      </ScSwitchLanguageButton>
      {isComponentVisible &&
        (isMobileDevice ? (
          <ModalSelect
            activeOption={currentLocale}
            alignBottom
            borderRoundIconOption={true}
            cols={1}
            icon={<IconComponent name="globeIcon" size="40px" />}
            onChange={handleChangeLanguage}
            onClose={toggleComponentVisible}
            options={availableLanguages}
            title="Locale"
          />
        ) : (
          <SwitchLanguageSelect
            availableLanguages={availableLanguages}
            disabled={pending}
            onChange={handleChangeLanguage}
            ref={ref}
            variant={variant}
          />
        ))}
    </ScSwitchLanguage>
  );
};
